import { ErrorState, InlineMessage, Spinner } from '@olytic/foundation';
import { AiRunLog, CapabilityList, ModelList, UsageBreakdown, UsageOverview, type UsageRange } from '@olytic/foundation/ai-admin';
import { useState } from 'react';

import { SETUP_ROUTES } from '../navigation';
import { CAPABILITIES_NOTE } from './aiCapabilitiesApi';
import { AiCreditsView, AiPage } from './AiPage';
import { formatTokens, rangeLabel, USAGE_DIMENSIONS } from './aiUsageApi';
import { useAiLimits } from './useAiLimits';
import { useAiCapabilities } from './useAiCapabilities';
import { useAiUsage } from './useAiUsage';

/**
 * AI management, built from @olytic/foundation/ai-admin: usage, credits and limits,
 * and which capabilities and models the server runs. Usage (GET /api/ai/usage),
 * this month's tokens against a monthly cap that only warns (GET and PUT
 * /api/ai/limits, MOTHER-79), and capabilities and models (GET /api/ai/capabilities;
 * each capability switched on or off with PUT /api/ai/capabilities/:capability; the
 * model is not changeable). Each page opens with a warning when usage is near or
 * over the cap.
 */

export const AI_ROUTES = {
  usage: SETUP_ROUTES.aiUsage,
  credits: SETUP_ROUTES.aiCredits,
  capabilities: SETUP_ROUTES.aiCapabilities,
} as const;

/* ── /setup/ai/usage ─────────────────────────────────────────────────── */

/**
 * Model tokens by day and capability over 7, 30 or 90 days, and the last calls.
 * By capability only, and no active users or run user: usage is not recorded per
 * person (MOTHER-53).
 */
export function AiUsagePage(): JSX.Element {
  const [range, setRange] = useState<UsageRange>('30d');
  const { load, retry } = useAiUsage(range);
  const view = load.state === 'ready' ? load.view : null;
  const loading = load.state === 'loading';
  const limits = useAiLimits();

  return (
    <AiPage title="Usage" note="Usage is counted in tokens." limits={limits.load}>
      {load.state === 'error' ? (
        <ErrorState title="Could not load AI usage" description={load.message} onRetry={retry} />
      ) : (
        <>
          {view?.empty ? (
            <InlineMessage tone="info">No AI usage was recorded in the last {rangeLabel(range)}.</InlineMessage>
          ) : null}
          <UsageOverview
            range={range}
            onRangeChange={setRange}
            series={view?.series ?? []}
            totals={view?.totals ?? { used: 0, runs: 0 }}
            prior={view?.prior}
            format={formatTokens}
            loading={loading}
          />
          <UsageBreakdown
            rows={view?.breakdown ?? []}
            dimensions={[...USAGE_DIMENSIONS]}
            dimension="capability"
            onDimensionChange={() => undefined}
            format={formatTokens}
            loading={loading}
          />
          <AiRunLog runs={view?.runs ?? []} loading={loading} />
        </>
      )}
    </AiPage>
  );
}

/* ── /setup/ai/credits ───────────────────────────────────────────────── */

/** Tokens used this month against a monthly cap that only warns (AiPage.tsx's AiCreditsView). */
export function AiCreditsPage(): JSX.Element {
  const { load, retry, save, saving, saveError, clearSaveError } = useAiLimits();
  return (
    <AiCreditsView
      load={load}
      onRetry={retry}
      onSave={save}
      saving={saving}
      saveError={saveError}
      onClearSaveError={clearSaveError}
    />
  );
}

/* ── /setup/ai/capabilities ──────────────────────────────────────────── */

/**
 * The capabilities the server runs, each switched on or off here (MOTHER-81),
 * and the model it uses, which has no Set default (MOTHER-70). The list is
 * disabled while a switch saves; a failed save puts the switch back and says why.
 */
export function AiCapabilitiesPage(): JSX.Element {
  const { load, retry, toggle, saving, saveError } = useAiCapabilities();
  const limits = useAiLimits();

  return (
    <AiPage title="Capabilities & models" note={CAPABILITIES_NOTE} limits={limits.load}>
      {load.state === 'error' ? (
        <ErrorState title="Could not load AI capabilities" description={load.message} onRetry={retry} />
      ) : load.state === 'loading' ? (
        <Spinner label="Loading AI capabilities…" />
      ) : (
        <>
          {load.view.unused ? (
            <InlineMessage tone="info">No AI capability was used in the last 30 days.</InlineMessage>
          ) : null}
          {saveError ? <InlineMessage tone="error" role="alert">Could not save the switch. {saveError}</InlineMessage> : null}
          <CapabilityList capabilities={load.view.capabilities} onToggle={toggle} disabled={saving} />
          <ModelList models={load.view.models} />
        </>
      )}
    </AiPage>
  );
}
